import React, { useState, useEffect, useRef, useCallback } from "react";
import styled from "styled-components";

import SAVED_SETTINGS from "../../utils/fluidBgConfig";
import { buildFluidHash } from "../../utils/fluidBgHash";
import {
  renderEyeToCanvas, meltEyeIntoMount, DEFAULT_EYE_SETTINGS,
} from "../../utils/fluidBgEye";
import CenterEye from "../TheEye/CenterEye";

// Older saved configs may predate the eye-melt settings (or newer keys
// inside them, like the eye's own pixelate)
const INITIAL_SETTINGS = {
  ...SAVED_SETTINGS,
  eye: { ...DEFAULT_EYE_SETTINGS, ...(SAVED_SETTINGS.eye || {}) },
};

// FLUID TUNER : a dev overlay rebuilt from the Fluid studio's designer UI
// (https://fluid.krackeddevs.com/) so the background can be tuned live on
// top of this site instead of round-tripping through their page. Renders
// its own full-screen canvas (same engine, via fluid-bg's mountNative) with
// the CenterEye vector in the middle, and every control pushes straight
// into the running engine with mount.set(). "Save configuration" persists
// the exact settings object into src/utils/fluidBgConfig.js through the
// dev-server endpoint in gatsby-config.js. Hide it by flipping
// SHOW_FLUID_TUNER in EntryEye.js once the look is locked in.

// Option lists mirror the engine's own tables (see fluid-bg/dist/core.js)
const FIELDS = [
  'noise', 'flow', 'cellular', 'gyroid', 'truchet', 'interfere', 'kaleido',
  'lines', 'grid', 'golden', 'smoke', 'crystal', 'honeycomb', 'bloom',
  'sweep', 'marble', 'plaid', 'curtain', 'stitch', 'pursuit', 'chladni',
  'cassini', 'topo', 'eddy',
];
const BLENDS = ['normal', 'multiply', 'screen', 'add', 'difference', 'overlay'];
const SCREENS = ['none', 'hex', 'ascii', 'dither', 'glitch'];
const MATERIALS = ['none', 'glass', 'metal', 'sand', 'liquid', 'molten', 'paint'];
const LENSES = [
  'none', 'square', 'invert', 'mobius', 'droste', 'hyperbolic', 'julia',
  'cube', 'exp', 'sine', 'joukowski', 'newton', 'modular', 'ground',
];

// Our config keeps mixes/lensAmt as 0-100; the engine's set() wants 0-1
const toEngineParams = (c) => ({
  speed: +c.speed, zoom: +c.zoom, warp: +c.warp, grain: +c.grain,
  sym: +c.sym, pixel: +c.pixel, dot: +c.dot, dots: c.dots ? 1 : 0,
  thresh: +c.thresh, seed: +c.seed,
  colors: [...c.colors],
  field: c.field,
  layer: c.layer.mix > 0
    ? { field: c.layer.field, blend: c.layer.blend, mix: c.layer.mix / 100 }
    : false,
  layer2: c.layer2.mix > 0
    ? { field: c.layer2.field, blend: c.layer2.blend, mix: c.layer2.mix / 100 }
    : false,
  screen: c.screen,
  material: c.material || 'none',
  lens: c.lens,
  lensAmt: (c.lensAmt == null ? 100 : c.lensAmt) / 100,
});

const TunerWrap = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9000;

  .tuner-canvas-host {
    position: absolute;
    inset: 0;
    background: #000;
  }

  .tuner-eye {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    pointer-events: none;
  }

  .tuner-error {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    color: #ff5577;
    font-family: monospace;
  }

  .tuner-panel {
    position: absolute;
    top: 12px;
    right: 12px;
    bottom: 12px;
    width: 300px;
    overflow-y: auto;
    background: rgba(8, 10, 14, 0.92);
    border: 1px solid #2a3540;
    border-radius: 8px;
    padding: 14px 16px 18px;
    color: #cfe3ea;
    font-family: monospace;
    font-size: 12px;

    h3 {
      font-family: 'pixel9', monospace;
      font-size: 15px;
      color: #0ff0ff;
      margin: 0 0 4px;
      letter-spacing: 1px;
    }

    .section-label {
      margin: 16px 0 6px;
      padding-top: 10px;
      border-top: 1px solid #1d2830;
      color: #6f8a99;
      font-size: 10px;
      letter-spacing: 2px;
      text-transform: uppercase;
    }

    .row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin: 5px 0;
      gap: 8px;

      label { flex: 0 0 72px; color: #8fb3c4; }
      input[type='range'] { flex: 1; min-width: 0; accent-color: #0ff0ff; }
      .val { flex: 0 0 44px; text-align: right; color: #0ff0ff; }

      select {
        flex: 1;
        background: #0d141b;
        color: #cfe3ea;
        border: 1px solid #2a3540;
        border-radius: 4px;
        padding: 3px 4px;
        font-family: monospace;
        font-size: 12px;
      }

      input[type='color'] {
        width: 44px;
        height: 26px;
        padding: 0;
        border: 1px solid #2a3540;
        border-radius: 4px;
        background: none;
        cursor: pointer;
      }

      input[type='text'].hex {
        flex: 1;
        background: #0d141b;
        color: #cfe3ea;
        border: 1px solid #2a3540;
        border-radius: 4px;
        padding: 3px 6px;
        font-family: monospace;
        font-size: 12px;
      }

      input[type='checkbox'] { accent-color: #0ff0ff; }
    }

    button {
      background: #0d141b;
      color: #0ff0ff;
      border: 1px solid #2a6573;
      border-radius: 4px;
      padding: 5px 10px;
      font-family: monospace;
      font-size: 12px;
      cursor: pointer;
      transition: all 0.15s ease-out;

      &:hover { background: #0ff0ff; color: #000; }
    }

    button.save {
      width: 100%;
      margin-top: 8px;
      padding: 9px 10px;
      font-family: 'pixel9', monospace;
      font-size: 15px;
      letter-spacing: 1px;
    }

    .status { margin-top: 6px; min-height: 16px; color: #6f8a99; }
    .status.ok { color: #35e08a; }
    .status.err { color: #ff5577; }

    .btn-row { display: flex; gap: 8px; margin-top: 8px; }
    .btn-row button { flex: 1; }
  }

  .tuner-close {
    position: absolute;
    top: 20px;
    left: 20px;
    background: rgba(8, 10, 14, 0.92);
    color: #cfe3ea;
    border: 1px solid #2a3540;
    border-radius: 6px;
    padding: 6px 12px;
    font-family: monospace;
    font-size: 13px;
    cursor: pointer;

    &:hover { color: #0ff0ff; }
  }
`;

const ReopenChip = styled.button`
  position: fixed;
  bottom: 14px;
  right: 14px;
  z-index: 9000;
  background: rgba(8, 10, 14, 0.92);
  color: #0ff0ff;
  border: 1px solid #2a6573;
  border-radius: 6px;
  padding: 7px 12px;
  font-family: monospace;
  font-size: 12px;
  cursor: pointer;
`;

const Slider = ({ label, value, min, max, step, onChange, fmt }) => (
  <div className="row">
    <label>{label}</label>
    <input type="range" min={min} max={max} step={step} value={value}
      onChange={e => onChange(parseFloat(e.target.value))} />
    <span className="val">{fmt ? fmt(value) : value}</span>
  </div>
);

const Select = ({ label, value, options, onChange }) => (
  <div className="row">
    <label>{label}</label>
    <select value={value} onChange={e => onChange(e.target.value)}>
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);

const FluidTuner = () => {
  const [config, setConfig] = useState(INITIAL_SETTINGS);
  const [hidden, setHidden] = useState(false);
  const [showEye, setShowEye] = useState(!INITIAL_SETTINGS.eye.melt);
  const [status, setStatus] = useState({ msg: '', kind: '' });
  const [glError, setGlError] = useState(false);
  const hostRef = useRef(null);
  const mountRef = useRef(null);
  const meltRef = useRef(null);

  // EYE MELT : (re)rasterize the eye and inject/update it in the engine —
  // see src/utils/fluidBgEye.js for the mechanics
  const syncMelt = useCallback((eyeCfg) => {
    if (!mountRef.current) return;
    if (!eyeCfg.melt) {
      if (meltRef.current) { meltRef.current.remove(); meltRef.current = null; }
      return;
    }
    renderEyeToCanvas(eyeCfg)
      .then(cv => {
        if (!mountRef.current) return;
        if (meltRef.current) {
          meltRef.current.setImage(cv);
          meltRef.current.setParams(eyeCfg);
        } else {
          meltRef.current = meltEyeIntoMount(mountRef.current, cv, eyeCfg);
          if (meltRef.current) {
            setStatus({ msg: 'eye melted into engine ✓', kind: 'ok' });
          } else {
            setStatus({ msg: 'engine shader patch failed — melt unavailable', kind: 'err' });
          }
        }
      })
      .catch(() => setStatus({ msg: 'eye rasterize failed', kind: 'err' }));
  }, []);

  // ENGINE : mount once into our host, then every change is a live set()
  useEffect(() => {
    let dead = false;
    if (hidden) return undefined;
    import("fluid-bg/dist/core.js").then(({ mountNative }) => {
      if (dead || !hostRef.current) return;
      try {
        mountRef.current = mountNative(hostRef.current);
        mountRef.current.set(toEngineParams(config));
        if (config.eye && config.eye.melt) syncMelt(config.eye);
      } catch (e) {
        setGlError(true);
      }
    });
    return () => {
      dead = true;
      meltRef.current = null; // dies with the mount
      if (mountRef.current) {
        mountRef.current.destroy();
        mountRef.current = null;
      }
    };
    // config intentionally omitted — updates go through update(), not remounts
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hidden]);

  const update = useCallback((patch) => {
    setConfig(prev => {
      const next = { ...prev, ...patch };
      if (mountRef.current) {
        try { mountRef.current.set(toEngineParams(next)); }
        catch (e) { setStatus({ msg: String(e.message), kind: 'err' }); }
      }
      return next;
    });
  }, []);

  const updateLayer = (key, patch) => update({ [key]: { ...config[key], ...patch } });

  // Eye changes bypass update() — they live outside the engine's set()
  // params. Geometry changes (melt/scale/drip) re-rasterize; blend/liquify
  // just retune the live uniforms.
  const updateEye = (patch) => {
    const eye = { ...config.eye, ...patch };
    setConfig(prev => ({ ...prev, eye }));
    if (patch.melt !== undefined || patch.scale !== undefined || patch.showDrip !== undefined) {
      syncMelt(eye);
      if (patch.melt) setShowEye(false); // melted eye replaces the DOM overlay one
    } else if (meltRef.current) {
      meltRef.current.setParams(eye);
    }
  };

  const setColor = (i, hex) => {
    const colors = [...config.colors];
    colors[i] = hex;
    update({ colors });
  };

  const shuffleSeed = () => update({ seed: +(3 + Math.random() * 89).toFixed(2) });

  // SAVE : persist the exact settings object into src/utils/fluidBgConfig.js
  // via the gatsby develop middleware (see gatsby-config.js)
  const saveConfig = () => {
    setStatus({ msg: 'saving…', kind: '' });
    fetch('/___fluid-bg/save', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(config),
    })
      .then(r => r.json())
      .then(r => {
        if (r.ok) setStatus({ msg: 'saved to fluidBgConfig.js ✓', kind: 'ok' });
        else setStatus({ msg: 'save failed: ' + r.error, kind: 'err' });
      })
      .catch(() => setStatus({
        msg: 'save failed — dev server only (gatsby develop)', kind: 'err',
      }));
  };

  const copy = (text, label) => {
    navigator.clipboard.writeText(text)
      .then(() => setStatus({ msg: label + ' copied ✓', kind: 'ok' }))
      .catch(() => setStatus({ msg: 'clipboard blocked', kind: 'err' }));
  };

  if (hidden) {
    return <ReopenChip onClick={() => setHidden(false)}>FLUID TUNER ◉</ReopenChip>;
  }

  return (
    <TunerWrap>
      <div className="tuner-canvas-host" ref={hostRef} />

      {glError &&
        <div className="tuner-error">WebGL unavailable — tuner canvas can't render</div>}

      {showEye &&
        <CenterEye className="tuner-eye" width={220} />}

      <button className="tuner-close" onClick={() => setHidden(true)}>
        ✕ hide tuner
      </button>

      <div className="tuner-panel">
        <h3>FLUID TUNER</h3>
        <div className="row">
          <label>overlay eye</label>
          <input type="checkbox" checked={showEye}
            onChange={e => setShowEye(e.target.checked)} />
        </div>

        <div className="section-label">02 · Colours (dark → light)</div>
        {config.colors.map((c, i) => (
          <div className="row" key={i}>
            <label>stop {i + 1}</label>
            <input type="color" value={c} onChange={e => setColor(i, e.target.value)} />
            <input type="text" className="hex" value={c}
              onChange={e => {
                const v = e.target.value;
                if (/^#[0-9a-fA-F]{6}$/.test(v)) setColor(i, v);
              }} />
          </div>
        ))}

        <div className="section-label">03 · Stack</div>
        <Select label="base" value={config.field} options={FIELDS}
          onChange={v => update({ field: v })} />

        <Select label="layer 2" value={config.layer.field} options={FIELDS}
          onChange={v => updateLayer('layer', { field: v })} />
        <Select label="blend" value={config.layer.blend} options={BLENDS}
          onChange={v => updateLayer('layer', { blend: v })} />
        <Slider label="mix" value={config.layer.mix} min={0} max={100} step={1}
          fmt={v => v + '%'} onChange={v => updateLayer('layer', { mix: v })} />

        <Select label="layer 3" value={config.layer2.field} options={FIELDS}
          onChange={v => updateLayer('layer2', { field: v })} />
        <Select label="blend" value={config.layer2.blend} options={BLENDS}
          onChange={v => updateLayer('layer2', { blend: v })} />
        <Slider label="mix" value={config.layer2.mix} min={0} max={100} step={1}
          fmt={v => v + '%'} onChange={v => updateLayer('layer2', { mix: v })} />

        <div className="section-label">03b · Eye — 4th layer (melted into engine)</div>
        <div className="row">
          <label>melt eye</label>
          <input type="checkbox" checked={!!config.eye.melt}
            onChange={e => updateEye({ melt: e.target.checked })} />
        </div>
        <Slider label="blend" value={config.eye.blend} min={0} max={100} step={1}
          fmt={v => v + '%'} onChange={v => updateEye({ blend: v })} />
        <Slider label="liquify" value={config.eye.liquify} min={0} max={100} step={1}
          fmt={v => v + '%'} onChange={v => updateEye({ liquify: v })} />
        <Slider label="scale" value={config.eye.scale} min={10} max={100} step={1}
          fmt={v => v + '%'} onChange={v => updateEye({ scale: v })} />
        <Slider label="pixelate" value={config.eye.pixel} min={1} max={40} step={1}
          fmt={v => (v <= 1 ? 'inherit' : v)} onChange={v => updateEye({ pixel: v })} />
        <div className="row">
          <label>drip</label>
          <input type="checkbox" checked={!!config.eye.showDrip}
            onChange={e => updateEye({ showDrip: e.target.checked })} />
        </div>

        <div className="section-label">04 · Look — motion &amp; space</div>
        <Slider label="speed" value={config.speed} min={0} max={4} step={0.05}
          onChange={v => update({ speed: v })} />
        <Slider label="zoom" value={config.zoom} min={0.3} max={8} step={0.05}
          onChange={v => update({ zoom: v })} />
        <Slider label="warp" value={config.warp} min={0} max={12} step={0.1}
          onChange={v => update({ warp: v })} />
        <Slider label="grain" value={config.grain} min={0} max={1} step={0.01}
          onChange={v => update({ grain: v })} />
        <Slider label="symmetry" value={config.sym} min={0} max={12} step={1}
          onChange={v => update({ sym: v })} />

        <div className="section-label">04 · Look — lens</div>
        <Select label="lens" value={config.lens} options={LENSES}
          onChange={v => update({ lens: v })} />
        <Slider label="amount" value={config.lensAmt} min={0} max={100} step={1}
          fmt={v => (v / 100).toFixed(2)} onChange={v => update({ lensAmt: v })} />

        <div className="section-label">04 · Look — surface</div>
        <Slider label="pixelate" value={config.pixel} min={1} max={40} step={1}
          onChange={v => update({ pixel: v })} />
        <Slider label="dot size" value={config.dot} min={1} max={40} step={1}
          onChange={v => update({ dot: v })} />
        <div className="row">
          <label>dot mode</label>
          <input type="checkbox" checked={!!config.dots}
            onChange={e => update({ dots: e.target.checked })} />
        </div>
        <Slider label="threshold" value={config.thresh} min={0} max={1} step={0.01}
          onChange={v => update({ thresh: v })} />
        <Select label="screen" value={config.screen} options={SCREENS}
          onChange={v => update({ screen: v })} />
        <Select label="finish" value={config.material || 'none'} options={MATERIALS}
          onChange={v => update({ material: v })} />

        <div className="section-label">Seed</div>
        <div className="row">
          <label>seed</label>
          <input type="range" min={0} max={92} step={0.1} value={config.seed}
            onChange={e => update({ seed: parseFloat(e.target.value) })} />
          <span className="val">{config.seed}</span>
        </div>
        <div className="btn-row">
          <button onClick={shuffleSeed}>↻ shuffle seed</button>
        </div>

        <div className="section-label">05 · Output</div>
        <button className="save" onClick={saveConfig}>SAVE CONFIGURATION</button>
        <div className="btn-row">
          <button onClick={() => copy(JSON.stringify(config, null, 2), 'config')}>
            copy config
          </button>
          <button onClick={() => copy(buildFluidHash(config), 'hash')}>
            copy hash
          </button>
        </div>
        <div className={`status ${status.kind}`}>{status.msg}</div>
      </div>
    </TunerWrap>
  );
};

export default FluidTuner;
